import { useCallback, useState } from 'react';

/**
 * A reusable hook for managing selection state of a set of items.
 * Provides toggle, toggleAll, and selection utilities.
 *
 * @param {Set<string>} initialSelected - Initial set of selected IDs
 * @returns {Object} Selection state and handlers
 */
function useSelectionState(initialSelected = new Set()) {
    const [selected, setSelected] = useState(initialSelected);

    /**
     * Toggle selection of a single item
     */
    const toggle = useCallback((id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    /**
     * Toggle all items based on current selection state.
     * No-op when allIds is empty (consistent with areAllSelected).
     *
     * @param {Array<string>} allIds - Array of all item IDs that can be selected
     */
    const toggleAll = useCallback((allIds) => {
        if (allIds.length === 0) return;

        setSelected((prev) => {
            const allSelected = allIds.every((id) => prev.has(id));
            if (allSelected) {
                return new Set();
            }
            return new Set(allIds);
        });
    }, []);

    /**
     * Select specific items
     *
     * @param {Array<string>|Set<string>} ids - IDs to select
     */
    const selectItems = useCallback((ids) => {
        setSelected(new Set(ids));
    }, []);

    /**
     * Clear all selections
     */
    const clear = useCallback(() => {
        setSelected(new Set());
    }, []);

    /**
     * Check if an item is selected
     *
     * @returns {boolean} True if the item is selected, false otherwise
     */
    const isSelected = useCallback((id) => selected.has(id), [selected]);

    /**
     * Check if all items are selected
     *
     * @param {Array<string>} allIds - Array of all item IDs
     * @returns {boolean} True if all items are selected, false otherwise
     */
    const areAllSelected = useCallback(
        (allIds) => allIds.length > 0 && allIds.every((id) => selected.has(id)),
        [selected],
    );

    return {
        selected,
        setSelected,
        toggle,
        toggleAll,
        selectItems,
        clear,
        isSelected,
        areAllSelected,
        size: selected.size,
    };
}

export default useSelectionState;
